/**
 * TERMINAL DE LA FINCA (30-10-2026, decisión de Pablo) — lo que la tablet
 * recuerda de sí misma.
 *
 * Al entrar con el usuario y la contraseña de la terminal (`terminal_entrar`, la
 * única forma desde el 30-10-2026; antes también «Abrir en esta tablet») la tablet
 * recibe UNA vez el secreto de su terminal y lo guarda aquí. No es una sesión: con él solo se puede leer el nombre de la
 * terminal y probar PINs en la edge `terminal-pin-login`.
 *
 * La sesión de la persona que mete el PIN es la de Supabase de siempre. Para
 * saber que esa sesión la abrió el PIN (y no es la del encargado que vinculó la
 * tablet y se quedó dentro) se apunta su id en `ACTIVE_KEY`: una sesión que no
 * coincide se cierra antes de enseñar nada.
 */

const DEVICE_KEY = "pest-terminal-device";
const ACTIVE_KEY = "pest-terminal-active-user";
/** Aviso en la MISMA pestaña (el `storage` del navegador solo llega a las otras). */
const CHANGE_EVENT = "pest-terminal-change";

/** Quien abrió la terminal en esta tablet: su contraseña es la que la cierra. */
export interface TerminalOwner {
  userId: string;
  /** El correo de su cuenta (también el interno de quien entra con usuario). */
  email: string;
  name: string;
}

export interface PairedTerminal {
  terminalId: string;
  secret: string;
  /** Falta en las tablets vinculadas antes del 30-10-2026 por la tarde. */
  owner?: TerminalOwner | null;
  /** Entró con el usuario de la terminal: se cierra con SU contraseña (20261030120000). */
  login?: string | null;
}

function notify(): void {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* ignore */
  }
}

/**
 * Para `useSyncExternalStore`: avisa cuando cambia la vinculación o quién está
 * dentro, en esta pestaña (evento propio) y en las demás (`storage`). Así todas las
 * vistas abiertas —el preview tiene tres— siguen a la vez al vincular o cerrar.
 */
export function subscribeTerminalDevice(cb: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === DEVICE_KEY || e.key === ACTIVE_KEY) cb();
  };
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb);
    window.removeEventListener("storage", onStorage);
  };
}

/** El texto guardado tal cual (instantánea estable para `useSyncExternalStore`). */
export function getPairedRaw(): string | null {
  try {
    return localStorage.getItem(DEVICE_KEY);
  } catch {
    return null;
  }
}

export function parsePaired(raw: string | null): PairedTerminal | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PairedTerminal>;
    if (typeof parsed.terminalId !== "string" || typeof parsed.secret !== "string") return null;
    const o = parsed.owner;
    const owner =
      o && typeof o.userId === "string" && typeof o.email === "string" && o.email
        ? { userId: o.userId, email: o.email, name: typeof o.name === "string" ? o.name : "" }
        : null;
    const login = typeof parsed.login === "string" && parsed.login ? parsed.login : null;
    return { terminalId: parsed.terminalId, secret: parsed.secret, owner, login };
  } catch {
    return null;
  }
}

export function getPairedTerminal(): PairedTerminal | null {
  return parsePaired(getPairedRaw());
}

export function setPairedTerminal(value: PairedTerminal): void {
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(value));
  } catch {
    /* sin almacenamiento no hay terminal */
  }
  notify();
}

export function clearPairedTerminal(): void {
  try {
    localStorage.removeItem(DEVICE_KEY);
    localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

/** ¿Esta tablet es una terminal? */
export function isTerminalDevice(): boolean {
  return getPairedTerminal() !== null;
}

export function getTerminalActiveUser(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

export function setTerminalActiveUser(userId: string | null): void {
  try {
    if (userId) localStorage.setItem(ACTIVE_KEY, userId);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

/**
 * Inactividad tras la que la terminal vuelve al teclado: 45 s, como Tactic (Pablo,
 * 30-10-2026: «no puede ser de 2 minutos»). Cada toque la reinicia.
 */
export const TERMINAL_IDLE_MS = 45_000;
/** Últimos segundos en los que se enseña la cuenta atrás. */
export const TERMINAL_IDLE_WARNING_MS = 15_000;

/** Segundos a pintar, o `null` si todavía no toca avisar (misma regla que Tactic). */
export function idleCountdownSeconds(msLeft: number): number | null {
  if (msLeft <= 0) return null;
  if (msLeft > TERMINAL_IDLE_WARNING_MS) return null;
  return Math.ceil(msLeft / 1000);
}
